import { z } from 'zod';

// Demo welcome flow (MYUA-17010). Everything here is used only when the instance
// setting `demo_welcome_mode_enabled` is true (see ./selectors.ts).

// Name of the personal project auto-created on the Demo instance
// (backend: DEMO_PERSONAL_PROJECT_NAME, crhub/settings.py).
export const DEMO_OWN_SPACE_NAME = 'Your space';

// Root folder of the personal project that receives uploads
// (backend: DEMO_FOLDER_FILES_NAME, crhub/constants.py).
export const DEMO_FILES_CATEGORY_NAME = 'Files';

// Special link from the mailing: asset_lab.html?demo_upload=1#/viewer/
// The param goes before the hash, like state_id / highlight_project_id.
export const DEMO_UPLOAD_PARAM = 'demo_upload';

// Upload intent survives an external sign-up/login page that may drop `?redirect=`.
export const DEMO_UPLOAD_INTENT_KEY = 'al.demoUploadIntent';
export const DEMO_UPLOAD_INTENT_TTL_MS = 7 * 24 * 3600 * 1000;

const DemoUploadIntentSchema = z.object({
  createdAt: z.number(),
});

export const parseLocationDemoUpload = (): boolean => {
  const url = new URL(window.location.href);
  return url.searchParams.get(DEMO_UPLOAD_PARAM) === '1';
};

// Drop the param so that F5 / "Back" do not reopen the upload modal.
export const removeDemoUploadParamFromLocation = () => {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(DEMO_UPLOAD_PARAM)) return;
  url.searchParams.delete(DEMO_UPLOAD_PARAM);
  window.history.replaceState(null, '', url.href);
};

// localStorage may be unavailable (private mode, blocked site data): then the
// flow relies on the URL param only.
export const saveDemoUploadIntent = () => {
  try {
    window.localStorage.setItem(DEMO_UPLOAD_INTENT_KEY, JSON.stringify({ createdAt: Date.now() }));
  } catch {
    // ignore
  }
};

export const readDemoUploadIntent = (): boolean => {
  try {
    const raw = window.localStorage.getItem(DEMO_UPLOAD_INTENT_KEY);
    if (raw === null) return false;
    const parsed = DemoUploadIntentSchema.safeParse(JSON.parse(raw));
    if (!parsed.success) return false;
    return Date.now() - parsed.data.createdAt < DEMO_UPLOAD_INTENT_TTL_MS;
  } catch {
    return false;
  }
};

export const clearDemoUploadIntent = () => {
  try {
    window.localStorage.removeItem(DEMO_UPLOAD_INTENT_KEY);
  } catch {
    // ignore
  }
};

export const normalizeName = (s: string) => s.trim().toLowerCase();

export const isDemoSpaceName = (name: string) =>
  normalizeName(name) === normalizeName(DEMO_OWN_SPACE_NAME);
